/**
 * HatchPatternManager - Manages hatch pattern availability and caching.
 *
 * Query and test available hatch patterns (predefined and custom).
 */

import type { DRDrawingViewer } from "../DRDrawingViewer";
import {
  isPatternAvailable,
  getAvailablePredefinedPatterns,
  clearPatternCache,
  PREDEFINED_PATTERNS,
  getCustomPatternNames,
} from "../database/hatch";

export class HatchPatternManager {
  constructor(private viewer: DRDrawingViewer) {}

  /**
   * List of known predefined pattern names.
   * Not all may be available in the current ODA version.
   */
  get knownPredefined(): readonly string[] {
    return PREDEFINED_PATTERNS;
  }

  /**
   * List of custom pattern names from DR_HATCHES.
   * These are always available as fallback.
   */
  get customPatterns(): string[] {
    return getCustomPatternNames();
  }

  /**
   * Test if a specific predefined pattern is available in ODA.
   * Results are cached for performance.
   * @param patternName Pattern name to test (e.g., 'ANSI31', 'SOLID')
   * @returns true if the pattern is available as predefined
   */
  isPredefinedAvailable(patternName: string): boolean {
    if (!this.viewer.database) {
      return false;
    }
    return isPatternAvailable(
      this.viewer.module,
      this.viewer.database,
      patternName,
    );
  }

  /**
   * Check if a pattern is available (either predefined or custom).
   * @param patternName Pattern name to check
   * @returns true if the pattern can be used
   */
  isAvailable(patternName: string): boolean {
    // SOLID is always available
    if (patternName === "SOLID") return true;

    // Check predefined
    if (this.viewer.database) {
      if (
        isPatternAvailable(
          this.viewer.module,
          this.viewer.database,
          patternName,
        )
      ) {
        return true;
      }
    }

    // Check custom patterns
    const customNames = getCustomPatternNames();
    return customNames.some(
      (n) => n.toUpperCase() === patternName.toUpperCase(),
    );
  }

  /**
   * Get list of all available predefined patterns.
   * Tests each known pattern and returns only available ones.
   * Results are cached, so subsequent calls are fast.
   * @returns Array of available predefined pattern names
   */
  getAvailablePredefined(): string[] {
    if (!this.viewer.database) {
      return [];
    }
    return getAvailablePredefinedPatterns(
      this.viewer.module,
      this.viewer.database,
    );
  }

  /**
   * Get all available patterns (predefined + custom).
   * @returns Array of all available pattern names
   */
  getAllAvailable(): string[] {
    const predefined = this.getAvailablePredefined();
    const custom = getCustomPatternNames();
    // Merge, removing duplicates (custom patterns may have same names as predefined)
    const all = new Set([...predefined, ...custom]);
    return Array.from(all).sort();
  }

  /**
   * Clear the pattern availability cache.
   * Call this when switching to a different database/file.
   */
  clearCache(): void {
    clearPatternCache();
  }
}
